import { sortByKey } from '@semios/app-platform-common'
import { NumberInput } from 'components/NumberInput/NumberInput'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'
import type { TIrrigationZoneEmitterId } from 'stores/fieldAssetStore/helperTypes'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { getAlternatingRowStyle, icsTableStyles } from '../_utils/icsTableStyles'
import { DayColumnHeader } from './DayColumnHeader'
import { formatHours } from './_utils/formatHours'
import type { TIrrigationScheduleTableProps } from './types'

export const IrrigationScheduleTable = ({
  emitterData,
  irrigationZoneEmitters,
  irrigationZoneNameDictionary,
  timezone,
  isEditMode,
  onDurationChange,
}: TIrrigationScheduleTableProps) => {
  const emitterLabel = mapApiEmitterTypeToLabel(emitterData.emitterType)

  const sortedZones = Object.values(emitterData.zones ?? {})
    .map((z) => ({
      ...z,
      zoneName: irrigationZoneNameDictionary[z.zoneId] ?? `Zone ${z.zoneId}`,
    }))
    .sort(sortByKey('zoneName'))

  const headerDates = sortedZones[0]?.dailyEvents ?? []

  return (
    <div>
      <h4 css={{ marginBottom: 16, textDecoration: 'underline' }}>{emitterLabel}</h4>
      <table css={icsTableStyles.table}>
        <thead>
          <tr>
            <th css={icsTableStyles.headerCell}>{translate.phrases.banyanApp('Zone Name').toUpperCase()}</th>
            {headerDates.map(({ date }) => (
              <th key={date} css={icsTableStyles.headerCell}>
                <DayColumnHeader day={moment.tz(date, timezone)} />
              </th>
            ))}
            <th css={icsTableStyles.headerCell}>{translate.phrases.banyanApp('Total').toUpperCase()}</th>
          </tr>
        </thead>

        <tbody>
          {sortedZones.map((zone, zoneIndex) => {
            const zoneTotalDuration = zone.dailyEvents.reduce((acc, { duration }) => acc + duration, 0)
            const zoneEmitterKey = `${zone.zoneId}|||${emitterData.emitterType}` as TIrrigationZoneEmitterId
            const flowRate = irrigationZoneEmitters?.[zoneEmitterKey]?.flowRate
            const mmPerHour = flowRate?.mmPerHour ?? 0
            const rateConverter = unitConverter.irrigationApplicationRate(mmPerHour)
            const rate = rateConverter.value() ?? 0
            const unitsPerHour = rateConverter.suffix()

            return (
              <tr key={zone.zoneId} css={getAlternatingRowStyle(zoneIndex)}>
                <td css={icsTableStyles.bodyCellLeft}>
                  <div>
                    <div css={{ fontWeight: 'bold' }}>{zone.zoneName}</div>
                    <div>
                      {rate > 0 ? (
                        <span css={{ fontSize: 12, color: colors.grey800 }}>{`${rate} ${unitsPerHour}`}</span>
                      ) : (
                        <span css={{ fontSize: 12, color: colors.grey800 }}>
                          {translate.phrases.banyanApp('No Application Rate')}
                        </span>
                      )}
                    </div>
                  </div>
                </td>

                {zone.dailyEvents.map(({ date, duration }) => {
                  return (
                    <td
                      key={`${zone.zoneId}-${date}`}
                      css={{ ...icsTableStyles.bodyCellData, padding: isEditMode ? 6 : 10 }}
                    >
                      {isEditMode ? (
                        <NumberInput
                          css={{ padding: 4, color: duration === 0 ? colors.grey800 : colors.midnight }}
                          min={0}
                          max={24}
                          step={1}
                          w={60}
                          m={'auto'}
                          hideControls
                          value={duration / 60}
                          onChange={(e) => {
                            const newDurationHours = Number(e) || 0
                            const newDurationMinutes = newDurationHours * 60

                            onDurationChange(emitterData.emitterType, zone.zoneId, date, newDurationMinutes)
                          }}
                        />
                      ) : (
                        <span css={{ color: duration === 0 ? colors.grey800 : colors.midnight }}>
                          {formatHours(duration)}
                        </span>
                      )}
                    </td>
                  )
                })}

                <td css={icsTableStyles.bodyCellHighlight}>{formatHours(zoneTotalDuration)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
